//@ts-expect-error - sa-vehicle-reg-num doesn't have type definitions
import saVRN from 'sa-vehicle-reg-num';
import { removeAllSpaces } from './regex';

/**
 * Look-alike letters people and vision models type for a plate letter.
 * `sa-vehicle-reg-num` accepts only the 17 plate letters and throws
 * "Invalid letter" on anything else, so «ا ص ي» or «أ ر ه» would read as
 * no plate at all. None of these is ever a plate letter, so folding only
 * rescues a misspelling, never changes a real plate.
 */
const PLATE_LETTER_FOLDS: Array<[RegExp, string]> = [
  [/[أإآٱ]/g, 'ا'],
  // ي, Persian/Urdu ی, ئ — the library only knows the V letter as ى.
  [/[يیئ]/g, 'ى'],
  // ة, Urdu ہ / ھ — the plate letter is ه.
  [/[ةہھ]/g, 'ه'],
  // Persian/Urdu keheh.
  [/ک/g, 'ك'],
  [/ؤ/g, 'و'],
  // Tashkeel and tatweel carry no letter.
  [/[\u064B-\u0652\u0670\u0640]/g, ''],
];

export const foldPlateLetters = (plate: string): string =>
  PLATE_LETTER_FOLDS.reduce((text, [pattern, letter]) => text.replace(pattern, letter), plate);

/**
 * The V letter as Hero writes it: «ي» (U+064A), not the library's «ى»
 * (U+0649). Bayan stores and accepts only «ي» — production refused every
 * trip sent with «ى» (`NAQL_INTEGRATION_ERROR`) and accepted the same
 * payload with «ي» (trip 60980833, stored as «اسي»). Input still folds to
 * «ى» because the library cannot parse «ي».
 */
const toHeroPlateLetters = (arLetters: string): string =>
  arLetters.replace(/هـ/g, 'ه').replace(/ى/g, 'ي');

/**
 *  this func helps decompose the plate number into numbers & letters
 * @param plate any plate value in Arabic or english KSF123 or 'م س ك 3213'
 * @returns the correct structure of the plate such as numbers & letters ... if there is no letters, it return empty string.
 */
export const getVehiclePlateNumber = (plate: string) => {
  const normalized = foldPlateLetters(plate);

  // letters
  const enLetters = saVRN.getEnLetters(normalized) as string;

  const arLetters = toHeroPlateLetters(saVRN.getArLetters(normalized));
  // numbers
  const enNumbers = removeAllSpaces(saVRN.getEnNumbers(normalized));
  const arNumbers = removeAllSpaces(saVRN.getArNumbers(normalized));

  // fullPlate
  const fullPlate = `${enLetters}${enNumbers}`;

  const displayAll = `${enLetters} | ${enNumbers} | ${arLetters}`;
  return {
    /** the full plate id such as BBF1234.
     *
     * It will always be in `English`.
     */
    fullPlate,

    /** letters of the plate such as BBA & ب ب ا */
    letters: {
      en: enLetters,
      ar: arLetters,
    },

    /** numbers of the plate such as 1234 & ١٢٣٤ */
    numbers: {
      en: enNumbers,
      ar: arNumbers,
    },
    displayAll,
  };
};

/**
 * Standalone words that mean "and not / but not" — a driver contrasting the
 * right plate with the wrong one ("ارر وليس ررا" = ARR and NOT RRA). The
 * letters BEFORE the marker are the ones they want; everything after is the
 * rejected reading and must be dropped, otherwise we'd grab the wrong order.
 */
const PLATE_NEGATION_MARKERS = new Set([
  'وليس',
  'وليست',
  'ليس',
  'ليست',
  'مو',
  'مب',
  'مش',
  'ماهي',
  'ماهو',
  'not',
]);

/** Words after the plate letters ("ا ع ص كذا", "ارر please") the walk-back skips. */
const MAX_TRAILING_WORDS = 3;

/**
 * Pull a Saudi plate out of a free-text message and return its canonical
 * `fullPlate` (Latin, e.g. `RRA6587`) — or `undefined` if there isn't one.
 *
 * Why this exists: Claude (and most LLMs) can read Arabic letters, but when
 * it forwards a plate through a tool call it often transliterates Arabic to
 * Latin character-by-character in the *wrong* direction. On Saudi plates the
 * canonical Latin form is the **reverse** of the Arabic character order
 * (`ارر` → `RRA`, not `ARR`), so a naive char-by-char transliteration flips
 * the plate to its mirror and the "update plate" tool call silently no-ops
 * (because the flipped Latin equals what's already on file). `sa-vehicle-reg-num`
 * already handles that reversal correctly — we just have to hand it the raw
 * user text instead of whatever the LLM re-encoded. Tools do exactly that:
 * they receive `ctx.userText` (the driver's verbatim message) and route it
 * through this helper, then use the result as the source of truth for the
 * plate — overruling the LLM's argument. See `proposeUpdateVehicleTool` in
 * `apps/backend/src/app/agent/agents/logisti/logisti.tools.ts`.
 *
 * @param text the driver's verbatim message
 * @param fallbackNumbers digits already on file (from the current plate), used
 *   when the driver corrects ONLY the letters and doesn't retype the numbers
 *   ("اللوحة غلط ارر"). Pass the `numbers.en` of the on-file plate.
 */
export const extractPlateFromText = (
  text: string | undefined,
  fallbackNumbers?: string,
): string | undefined => {
  if (!text) return undefined;
  let working = text.trim();
  if (!working) return undefined;

  // Drop the rejected reading in a contrast like "ارر وليس ررا" — keep only
  // the affirmative letters before the negation marker.
  const rawTokens = working.split(/\s+/).filter(Boolean);
  const negIdx = rawTokens.findIndex(t => PLATE_NEGATION_MARKERS.has(t.toLowerCase()));
  if (negIdx > 0) working = rawTokens.slice(0, negIdx).join(' ');
  // After the markers: folding ي → ى would turn «وليس» into no marker.
  working = foldPlateLetters(working);

  const attempts: string[] = [];

  // Insert a space between adjacent letters and digits so `ارر٦٥٨٧` and
  // `KSZ1234` tokenize the same as `ارر ٦٥٨٧` / `KSZ 1234`.
  const respaced = working
    .replace(/([\u0621-\u064Aa-zA-Z])([0-9\u0660-\u0669])/gu, '$1 $2')
    .replace(/([0-9\u0660-\u0669])([\u0621-\u064Aa-zA-Z])/gu, '$1 $2');

  const tokens = respaced.split(/\s+/).filter(Boolean);

  // Find the last digit-only token (the plate number, 3-4 digits Latin or
  // Arabic-Indic).
  let digitTokenIndex = -1;
  for (let i = tokens.length - 1; i >= 0; i--) {
    if (/^[0-9\u0660-\u0669]{3,4}$/.test(tokens[i])) {
      digitTokenIndex = i;
      break;
    }
  }

  // The digits the letters pair with: from the message when present,
  // otherwise the numbers already on file (letters-only correction).
  const digits = digitTokenIndex >= 0 ? tokens[digitTokenIndex] : fallbackNumbers;
  // Where the trailing letter run ends: just before the digit block, or at
  // the end of the message when there is no digit block.
  const lettersEnd = digitTokenIndex >= 0 ? digitTokenIndex : tokens.length;

  // Walk back from the digit block (or end) through adjacent letter tokens,
  // building progressively longer letter candidates. Try each one — the first
  // that yields a 3-letter plate through the library wins. This tolerates
  // surrounding words like "اللوحة غلط" or "the plate is" without letting them
  // corrupt the letter extraction. The walk also restarts past up to
  // MAX_TRAILING_WORDS words after the letters — «عدل اللوحه ا ع ص كذا»
  // ends in «كذا», which no plate parses.
  if (digits) {
    const lowestEnd = Math.max(1, lettersEnd - MAX_TRAILING_WORDS);
    for (let end = lettersEnd; end >= lowestEnd; end--) {
      const letterTokens: string[] = [];
      for (let i = end - 1; i >= 0; i--) {
        const tok = tokens[i];
        if (!/^[\u0621-\u064Aa-zA-Z]+$/u.test(tok)) break;
        letterTokens.unshift(tok);
        attempts.push(`${letterTokens.join(' ')} ${digits}`);
      }
    }
  }

  // Last resort: hand the whole (affirmative) message to the library, with and
  // without the fallback numbers appended.
  attempts.push(working);
  if (fallbackNumbers) attempts.push(`${working} ${fallbackNumbers}`);

  for (const candidate of attempts) {
    try {
      const parsed = getVehiclePlateNumber(candidate);
      if (
        parsed.letters.en &&
        parsed.letters.en.length === 3 &&
        parsed.numbers.en &&
        parsed.numbers.en.length >= 3 &&
        parsed.numbers.en.length <= 4
      ) {
        return parsed.fullPlate;
      }
    } catch {
      /* try next candidate */
    }
  }
  return undefined;
};

/**
 * A plate as `displayAll` (`KSZ | 1234 | م س ك`), safe on anything.
 *
 * The dual script is the point: a driver reads the Arabic half off the
 * plate and a dispatcher types the Latin half into a system, so a
 * surface that carries only one of them makes somebody transliterate.
 * Every place a user sees a plate — dashboard tables, the Bayan
 * confirmation card, the وثيقة WhatsApp templates — goes through here,
 * so a plate never looks different depending on where it is shown.
 *
 * ## Arabic direction stays in `sa-vehicle-reg-num`'s canonical form
 *
 * The library returns Arabic in physical left-to-right order on the
 * plate — the same order it accepts as **input** (`getVehiclePlateNumber('م س ك 1234').letters.en === 'KSZ'`).
 * Bayan consumes the same canonical form via `decomposeBayanPlate`.
 * The two round-trip cleanly: parse any user-typed plate → canonical
 * Latin, format canonical Latin → the same Arabic string the user
 * typed. That symmetry is important: if we flipped the display,
 * anyone who read it and copied it back (`م س ك 1234`) would land the
 * mirror plate (`ZSK1234`) — a silent data-corruption bug worse than
 * the UX confusion.
 *
 * The naming-order confusion when a driver reads a plate in Arabic
 * reading direction (right-to-left) and the display shows physical
 * LTR is real, but it belongs in prompt handling and in
 * `extractPlateFromText` (which trusts the driver's raw text), not
 * here.
 *
 * Falls back to the trimmed input when the parser finds no letters or
 * no digits. A foreign or malformed plate would otherwise render as the
 * separators alone (`" |  | "`), which is worse than showing what is
 * actually stored, and `saVRN` throws on some inputs rather than
 * returning empty.
 */
export const formatPlateDualScript = (plate: string | undefined): string => {
  const trimmed = plate?.trim() ?? '';
  if (!trimmed) return trimmed;
  try {
    const parsed = getVehiclePlateNumber(trimmed);
    if (!parsed.letters.en || !parsed.letters.ar || !parsed.numbers.en) return trimmed;
    return parsed.displayAll;
  } catch {
    return trimmed;
  }
};
